import { useState, useEffect } from 'react';
import { Settings, Camera, MapPin, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Globe, HelpCircle } from 'lucide-react';
import CameraTransitions from './CameraTransitions';
import VehicleRouteAnalysis from './VehicleRouteAnalysis';
import lprJourneyAPI from '../../../../services/lprJourneyAPI';
import './LprJourneyMain.css';

/**
 * LprJourneyMain
 *
 * The main view shown once camera coordinates are configured.
 * Displays a summary of the configuration and provides a "Configure Map" button
 * to re-open the setup modal.
 *
 * Props:
 *   config           {object}   - { cameras, hasBackgroundImage, isFullyConfigured }
 *   cameras          {Array}    - cameras from CAMERAS table
 *   networkName      {string}   - display name of the selected network
 *   onConfigureMap   {function} - opens the CameraSetupModal
 *   onShowHelp       {function} - opens the LPR Journey feature intro
 */
/**
 * Return today's date string (YYYY-MM-DD) in the given IANA timezone.
 * Falls back to the local system date when timezone is not yet known.
 */
const todayInTimezone = (timezone) => {
  if (!timezone) return new Date().toISOString().slice(0, 10);
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
};

/**
 * Return the UTC offset (in whole minutes) for a given IANA timezone at the
 * current moment.  Returns null when the timezone string is invalid.
 */
const getUtcOffsetMinutes = (timezone) => {
  if (!timezone) return null;
  try {
    // Format a reference instant in both UTC and the target timezone, then diff.
    const now = new Date();
    const utcStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'UTC',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(now);
    const tzStr = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(now);
    const toMs = (s) => new Date(s.replace(',', '')).getTime();
    return Math.round((toMs(tzStr) - toMs(utcStr)) / 60000);
  } catch {
    return null;
  }
};

/** Format a UTC offset in minutes to a human-readable string like "+5:30" or "−8:00". */
const fmtOffset = (minutes) => {
  if (minutes === null) return '';
  const sign = minutes >= 0 ? '+' : '−';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `${sign}${h}${m > 0 ? `:${String(m).padStart(2, '0')}` : ''}`;
};

/**
 * Inline banner that surfaces the network timezone and the offset difference
 * between that timezone and the user's local browser timezone.
 * Renders nothing while the network timezone is still loading.
 */
const TimezoneInfo = ({ networkTimezone }) => {
  const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const networkOffset  = getUtcOffsetMinutes(networkTimezone);
  const clientOffset   = getUtcOffsetMinutes(clientTimezone);

  if (!networkTimezone || networkOffset === null || clientOffset === null) return null;

  const diffMinutes = networkOffset - clientOffset;
  const isSame      = diffMinutes === 0;

  const diffLabel = isSame
    ? 'same as your local time'
    : `${fmtOffset(Math.abs(diffMinutes))} hr${Math.abs(diffMinutes) !== 60 ? 's' : ''} ${diffMinutes > 0 ? 'ahead of' : 'behind'} your local time`;

  const networkLabel = `${networkTimezone} (UTC${fmtOffset(networkOffset)})`;
  const clientLabel  = `${clientTimezone} (UTC${fmtOffset(clientOffset)})`;

  return (
    <div className={`lpr-main-tz-banner ${isSame ? 'lpr-main-tz-banner--same' : 'lpr-main-tz-banner--diff'}`}>
      <Globe size={14} className="lpr-main-tz-icon" />
      <span className="lpr-main-tz-text">
        <span className="lpr-main-tz-label">Network timezone:</span>
        <span className="lpr-main-tz-value">{networkLabel}</span>
        <span className="lpr-main-tz-sep">·</span>
        <span className="lpr-main-tz-label">Your timezone:</span>
        <span className="lpr-main-tz-value">{clientLabel}</span>
        {!isSame && (
          <>
            <span className="lpr-main-tz-sep">·</span>
            <span className="lpr-main-tz-diff">
              Network is {diffLabel}
            </span>
          </>
        )}
      </span>
    </div>
  );
};

const LprJourneyMain = ({ config, cameras = [], networkName, networkId, onConfigureMap, onShowHelp }) => {
  /** IANA timezone for the selected network (fetched once per network change). */
  const [networkTimezone, setNetworkTimezone] = useState(null);

  useEffect(() => {
    if (!networkId) return;
    setNetworkTimezone(null);
    lprJourneyAPI.getNetworkTimezone(networkId).then(setNetworkTimezone);
  }, [networkId]);

  /**
   * Shared date sliding window — owned here so both CameraTransitions and
   * VehicleRouteAnalysis always show the same selected day.
   * Initialised to today in the network's timezone once it is known;
   * until then we use the system local date as a safe default.
   */
  const [selectedDay, setSelectedDay] = useState(
    () => new Date().toISOString().slice(0, 10)
  );

  // Reset to today (in the correct timezone) whenever the network changes.
  // This runs immediately on networkId change (using local date as a safe
  // default) and again once the timezone resolves to the precise network date.
  useEffect(() => {
    setSelectedDay(todayInTimezone(networkTimezone));
  }, [networkId, networkTimezone]); // eslint-disable-line react-hooks/exhaustive-deps

  const placedCameras = config?.cameras?.filter(c => c.pos_x !== null && c.pos_y !== null) || [];
  const totalCameras = cameras.length;
  const placedCount = placedCameras.length;
  const isFullyConfigured = config?.isFullyConfigured || false;
  const isUnconfigured = placedCount === 0 && !config?.hasBackgroundImage;

  // Collapse "Cameras in Network" by default when the map is fully configured or unconfigured
  const [cameraNetworkOpen, setCameraNetworkOpen] = useState(!isFullyConfigured && !isUnconfigured);

  return (
    <div className="lpr-main-container">
      {/* ── Config status banner ─────────────────────────────────────────── */}
      <div className={`lpr-main-status-banner ${isFullyConfigured ? 'lpr-main-status-ok' : 'lpr-main-status-partial'}${isUnconfigured ? ' lpr-main-status-banner--unconfigured' : ''}`}>
        <div className="lpr-main-status-left">
          {isFullyConfigured ? (
            <CheckCircle size={18} className="lpr-main-status-icon lpr-main-status-icon-ok" />
          ) : (
            <AlertCircle size={18} className="lpr-main-status-icon lpr-main-status-icon-warn" />
          )}
          <div>
            <p className="lpr-main-status-title">
              {isFullyConfigured
                ? 'Camera map fully configured'
                : `${placedCount} of ${totalCameras} cameras placed on map`}
            </p>
            <p className="lpr-main-status-subtitle">
              {config?.hasBackgroundImage
                ? 'Background map image is set'
                : 'No background map image uploaded'}
            </p>
          </div>
        </div>
        <div className="lpr-main-status-actions">
          {onShowHelp && (
            <button className="lpr-main-help-btn" onClick={onShowHelp} title="What is LPR Journey?" aria-label="Learn about LPR Journey">
              <HelpCircle size={15} />
              What is LPR Journey?
            </button>
          )}
          <button className="lpr-main-configure-btn" onClick={onConfigureMap}>
            <Settings size={15} />
            Configure Map
          </button>
        </div>
      </div>

      {/* ── Timezone awareness banner ────────────────────────────────────── */}
      <TimezoneInfo networkTimezone={networkTimezone} />

      {/* ── Camera grid ──────────────────────────────────────────────────── */}
      <div className="lpr-main-section">
        <button
          className="lpr-main-section-toggle"
          onClick={() => setCameraNetworkOpen(o => !o)}
          aria-expanded={cameraNetworkOpen}
        >
          <span className="lpr-main-section-title lpr-main-section-title--inline">
            <Camera size={16} />
            Cameras in Network
            {networkName && <span className="lpr-main-section-network">— {networkName}</span>}
          </span>
          {cameraNetworkOpen ? <ChevronUp size={16} className="lpr-main-section-chevron" /> : <ChevronDown size={16} className="lpr-main-section-chevron" />}
        </button>

        {cameraNetworkOpen && (
          cameras.length === 0 ? (
            <div className="lpr-main-empty">
              <Camera size={32} className="lpr-main-empty-icon" />
              <p>No cameras found for this network</p>
            </div>
          ) : (
            <div className="lpr-main-camera-grid">
              {cameras.map(cam => {
                const savedPos = config?.cameras?.find(c => c.camera_id === cam.camera_id);
                const isPlaced = savedPos && savedPos.pos_x !== null;

                return (
                  <div
                    key={cam.camera_id}
                    className={`lpr-main-camera-card ${isPlaced ? 'lpr-main-camera-card-placed' : 'lpr-main-camera-card-unplaced'}`}
                  >
                    <div className="lpr-main-camera-card-header">
                      <span className="lpr-main-camera-card-icon">📷</span>
                      <span className={`lpr-main-camera-card-status ${isPlaced ? 'lpr-main-camera-card-status-placed' : 'lpr-main-camera-card-status-unplaced'}`}>
                        {isPlaced ? '✓ Placed' : 'Not placed'}
                      </span>
                    </div>
                    <p className="lpr-main-camera-card-name">{cam.camera_name}</p>
                    <p className="lpr-main-camera-card-id">{cam.camera_id}</p>
                    {isPlaced && (
                      <div className="lpr-main-camera-card-coords">
                        <MapPin size={10} />
                        <span>
                          ({(savedPos.pos_x * 100).toFixed(1)}%, {(savedPos.pos_y * 100).toFixed(1)}%)
                        </span>
                      </div>
                    )}
                    <div className="lpr-main-camera-card-tags">
                      {cam.is_entry_point === 'true' && (
                        <span className="lpr-main-camera-tag lpr-main-camera-tag-entry">Entry</span>
                      )}
                      {cam.is_exit_point === 'true' && (
                        <span className="lpr-main-camera-tag lpr-main-camera-tag-exit">Exit</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* ── Camera Transitions ────────────────────────────────────────────── */}
      <div className="lpr-main-section">
        <CameraTransitions
          networkId={networkId}
          networkName={networkName}
          networkTimezone={networkTimezone}
          config={config}
          cameras={cameras}
          selectedDay={selectedDay}
          onDayChange={setSelectedDay}
          onConfigureMap={onConfigureMap}
          onShowHelp={onShowHelp}
        />
      </div>

      {/* ── Vehicle Route Analysis ────────────────────────────────────────── */}
      <div className="lpr-main-section">
        <VehicleRouteAnalysis
          networkId={networkId}
          networkName={networkName}
          networkTimezone={networkTimezone}
          config={config}
          cameras={cameras}
          selectedDay={selectedDay}
          onDayChange={setSelectedDay}
          onConfigureMap={onConfigureMap}
          onShowHelp={onShowHelp}
        />
      </div>
    </div>
  );
};

export default LprJourneyMain;
